import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { navigationAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Navigation, ExternalLink, GripVertical } from 'lucide-react';

const emptyLink = {
  label: '',
  url: '',
  location: 'header',
  order: 0,
  is_external: false,
};

export const AdminNavigation = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState(emptyLink);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await navigationAPI.getAll();
      setLinks(response.data);
    } catch (e) {
      toast.error('Failed to load navigation');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingLink(null);
    setFormData(emptyLink);
    setDialogOpen(true);
  };

  const openEditDialog = (link) => {
    setEditingLink(link);
    setFormData(link);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingLink) {
        await navigationAPI.update(editingLink.id, formData);
        toast.success('Link updated!');
      } else {
        await navigationAPI.create(formData);
        toast.success('Link created!');
      }
      setDialogOpen(false);
      fetchLinks();
    } catch (e) {
      toast.error('Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    try {
      await navigationAPI.delete(id);
      toast.success('Link deleted!');
      fetchLinks();
    } catch (e) {
      toast.error('Failed to delete link');
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const headerLinks = links.filter((l) => l.location === 'header').sort((a, b) => a.order - b.order);
  const footerLinks = links.filter((l) => l.location === 'footer').sort((a, b) => a.order - b.order);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-blue-600" />
      </div>
    );
  }

  const LinkItem = ({ link }) => (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
      <div className="flex items-center gap-3">
        <GripVertical className="w-4 h-4 text-slate-400" />
        <div>
          <p className="font-medium text-slate-900">{link.label}</p>
          <p className="text-sm text-slate-500 flex items-center gap-1">
            {link.url}
            {link.is_external && <ExternalLink className="w-3 h-3" />}
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={() => openEditDialog(link)}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-red-600 hover:text-red-700"
          onClick={() => handleDelete(link.id)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="admin-navigation">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Navigation</h1>
          <p className="mt-2 text-slate-600">Manage header and footer links</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} data-testid="add-nav-link-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Link
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingLink ? 'Edit Link' : 'Add Link'}</DialogTitle>
              <DialogDescription>Configure navigation links</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Label</Label>
                <Input
                  value={formData.label}
                  onChange={(e) => updateField('label', e.target.value)}
                  placeholder="About Us"
                  data-testid="nav-label-input"
                />
              </div>
              <div className="space-y-2">
                <Label>URL</Label>
                <Input
                  value={formData.url}
                  onChange={(e) => updateField('url', e.target.value)}
                  placeholder="/page/about-us or https://..."
                  data-testid="nav-url-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select
                    value={formData.location}
                    onValueChange={(value) => updateField('location', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="header">Header</SelectItem>
                      <SelectItem value="footer">Footer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => updateField('order', parseInt(e.target.value))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_external}
                  onCheckedChange={(checked) => updateField('is_external', checked)}
                />
                <Label>External Link (opens in new tab)</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-nav-btn">
                  {saving ? 'Saving...' : 'Save Link'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Header Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Navigation className="w-5 h-5" />
              Header Links
            </CardTitle>
            <CardDescription>Links displayed in the top navigation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {headerLinks.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No header links</p>
            ) : (
              headerLinks.map((link) => <LinkItem key={link.id} link={link} />)
            )}
          </CardContent>
        </Card>

        {/* Footer Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Navigation className="w-5 h-5" />
              Footer Links
            </CardTitle>
            <CardDescription>Links displayed in the footer</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {footerLinks.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No footer links</p>
            ) : (
              footerLinks.map((link) => <LinkItem key={link.id} link={link} />)
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminNavigation;
